import {BehaviorSubject} from 'rxjs';
import {debounceTime, distinctUntilChanged, map} from 'rxjs/operators';

class ApiRegistry {
	// Default debounce time in milliseconds
	#debounceTime = 500;

	// Map to track active API calls with their counts
	#activeRequests = new Map();

	// BehaviorSubject that emits the current state of active requests
	#requestsSubject = new BehaviorSubject(new Map(this.#activeRequests));

	// Observable that emits true when all requests are settled
	// Uses debounceTime to prevent quick flashes when API calls occur in quick succession
	settled$ = this.#requestsSubject.pipe(
		map((requests) => requests.size === 0),
		distinctUntilChanged(), // Only emit when the settled state changes
		debounceTime(this.#debounceTime) // Wait before emitting the settled state
	);

	// Observable that emits the current active requests
	activeRequests$ = this.#requestsSubject.asObservable();

	// Register a new API request
	registerRequest(route) {
		const count = this.#activeRequests.get(route) || 0;
		this.#activeRequests.set(route, count + 1);
		this.#requestsSubject.next(new Map(this.#activeRequests));
		return route;
	}

	// Complete an API request
	completeRequest(route) {
		const count = this.#activeRequests.get(route);
		if (count === undefined) return;

		if (count <= 1) {
			this.#activeRequests.delete(route);
		} else {
			this.#activeRequests.set(route, count - 1);
		}

		this.#requestsSubject.next(new Map(this.#activeRequests));
	}

	// Check if application is settled
	isSettled() {
		return this.#activeRequests.size === 0;
	}

	// Set the debounce time (in milliseconds)
	setDebounceTime(ms) {
		if (typeof ms === 'number' && ms >= 0) {
			this.#debounceTime = ms;
			// Recreate the settled$ observable with the new debounce time
			this.settled$ = this.#requestsSubject.pipe(
				map((requests) => requests.size === 0),
				distinctUntilChanged(),
				debounceTime(this.#debounceTime)
			);
		}
	}

	// Get the current debounce time
	getDebounceTime() {
		return this.#debounceTime;
	}

	// Get the current list of active requests with their counts
	getActiveRequests() {
		return Object.fromEntries(this.#activeRequests);
	}
}

// Singleton instance
export const apiRegistry = new ApiRegistry();
